/**
 * CSV ↔ JSON — pure, deterministic conversion. Safe to render server-side.
 *
 * The CSV parser is RFC 4180-ish: it handles double-quoted fields, commas and
 * newlines inside quotes, and "" as an escaped quote. Parsing is lenient — an
 * unterminated quote keeps whatever was read rather than throwing.
 */

export type CsvToJsonResult =
  | { ok: true; data: Array<Record<string, string>> | string[][] }
  | { ok: false; error: string };

export type JsonToCsvResult =
  | { ok: true; csv: string }
  | { ok: false; error: string };

/** Parse CSV text into a grid of string cells. */
export function parseCsv(input: string, delimiter = ","): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  let i = 0;
  const n = input.length;

  while (i < n) {
    const c = input[i];

    if (inQuotes) {
      if (c === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === delimiter) {
      row.push(field);
      field = "";
      i++;
      continue;
    }
    if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i++;
      continue;
    }
    if (c === "\r") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i += input[i + 1] === "\n" ? 2 : 1;
      continue;
    }
    field += c;
    i++;
  }

  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

export function csvToJson(
  input: string,
  opts: { header?: boolean; delimiter?: string } = {},
): CsvToJsonResult {
  const { header = true, delimiter = "," } = opts;
  if (!input.trim()) return { ok: false, error: "Input is empty." };

  const rows = parseCsv(input, delimiter);
  if (rows.length === 0) return { ok: false, error: "No rows found." };
  if (!header) return { ok: true, data: rows };

  const [head, ...body] = rows;
  const data = body.map((r) => {
    const obj: Record<string, string> = {};
    head.forEach((key, idx) => {
      obj[key] = r[idx] ?? "";
    });
    return obj;
  });
  return { ok: true, data };
}

function quoteField(value: string, delimiter: string): string {
  if (
    value.includes(delimiter) ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function jsonToCsv(
  jsonText: string,
  opts: { delimiter?: string } = {},
): JsonToCsvResult {
  const { delimiter = "," } = opts;
  if (!jsonText.trim()) return { ok: false, error: "Input is empty." };

  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonText);
  } catch (e) {
    return { ok: false, error: `Invalid JSON: ${(e as Error).message}` };
  }
  if (!Array.isArray(parsed)) {
    return { ok: false, error: "JSON must be an array of objects." };
  }
  if (parsed.length === 0) return { ok: true, csv: "" };

  const keys: string[] = [];
  const seen = new Set<string>();
  for (const item of parsed) {
    if (item === null || typeof item !== "object" || Array.isArray(item)) {
      return { ok: false, error: "Every array item must be an object." };
    }
    for (const k of Object.keys(item)) {
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
  }

  const headerLine = keys.map((k) => quoteField(k, delimiter)).join(delimiter);
  const lines = parsed.map((item) =>
    keys
      .map((k) => {
        const v = Object.hasOwn(item as object, k)
          ? (item as Record<string, unknown>)[k]
          : "";
        const cell =
          v === null || v === undefined
            ? ""
            : typeof v === "object"
              ? JSON.stringify(v)
              : String(v);
        return quoteField(cell, delimiter);
      })
      .join(delimiter),
  );
  return { ok: true, csv: [headerLine, ...lines].join("\n") };
}
